/*
 * Gift Card Balance Component
 *
 * Handles checking gift card balances by:
 * - Capturing form submission with gift card code and PIN
 * - Making API request to AP21 to check balance
 * - Displaying balance results or error messages
 * - Managing loading states during request
 *
 * Components:
 * - Form (#giftCardBalanceForm)
 * - Code input (#giftCardBalanceCode)
 * - PIN input (#giftCardBalancePin)
 * - Submit button (#giftCardBalanceSubmit)
 * - Support link (#giftCardBalanceSupport)
 * - Results container (#giftCardBalanceResult)
 */

const giftCardForm = document.querySelector('#giftCardBalanceForm')
const giftCardCodeInput = document.querySelector('#giftCardBalanceCode')
const giftCardBalancePin = document.querySelector('#giftCardBalancePin')
const submitBtn = document.querySelector('#giftCardBalanceSubmit')
const balanceSupportLinkElement = document.querySelector('#giftCardBalanceSupport')
const balanceSupportLink = balanceSupportLinkElement ? balanceSupportLinkElement.value : null
const giftCardBalanceResult = document.querySelector('#giftCardBalanceResult')

giftCardForm.addEventListener('submit', (e) => {
  e.preventDefault()
  const code = giftCardCodeInput.value
  const pin = giftCardBalancePin.value

  setLoadingState(submitBtn, true)

  getBalance(code, pin)
    .then(response => {
      renderGiftCardBalance(response)
    })
    .catch(error => {
      const errorMarkup = `<div class="flex items-center justify-between gap-x-0.5 w-full text-type-error bg-page-error p-1 mt-2.5 lg:mt-1.5">
        <p class="paragraph-s font-bold">${error.message.startsWith('Unexpected token') ? 'Something went wrong.' : error.message}</p>
        <a href="${balanceSupportLink}" class="shrink-0 text-xs link-fade" aria-label="Contact us here">Contact us here</a>
      </div>`
      giftCardBalanceResult.innerHTML = errorMarkup
    })
    .finally(() => {
      setLoadingState(submitBtn, false)
    })
})

function setLoadingState(button, isLoading) {
  button.setAttribute('disabled', true)
  button.querySelector('.btn-loading-overlay').classList.remove('!hidden')

  if (isLoading) {
    button.setAttribute('disabled', true)
  } else {
    button.removeAttribute('disabled')
    button.querySelector('.btn-loading-overlay').classList.add('!hidden')
  }
}

function renderGiftCardBalance(response) {
  if (!response || !response.data) return

  const { voucherNumber, expiryDate, originalAmount, usedAmount, availableAmount } = response.data
  const expiry = new Date(expiryDate)
  const isExpired = expiry < new Date()

  const giftCardBalanceResult = document.querySelector('#giftCardBalanceResult')
  if (!giftCardBalanceResult) return

  const giftCardBalanceResultHTML = `
    <div class="flex flex-col gap-y-1.5 p-1 lg:p-1.5 bg-page-secondary mt-2.5 lg:mt-1.5">
      <h3 class="paragraph-m font-bold">${isExpired ? 'Your Gift Card Has Expired' : 'Your Gift Card Balance'}</h3>
      <ul class="flex flex-col gap-y-0.5 children:list-none">
        <li>
          <p class="paragraph-m">Gift Card Code: ${voucherNumber}</p>
        </li>
        <li>
          <p class="paragraph-m">Expiry Date: ${formatDate(expiry)}</p>
        </li>
        <li>
          <p class="paragraph-m">Original Amount: $${originalAmount}</p>
        </li>
        <li>
          <p class="paragraph-m">Used Amount: $${usedAmount}</p>
        </li>
      </ul>
      <p class="paragraph-m font-bold">Current Balance: $${availableAmount}</p>
    </div>
  `

  giftCardBalanceResult.innerHTML = giftCardBalanceResultHTML
}

async function getBalance(number, pin) {
  try {
    const response = await fetch(`${window.location.origin}/apps/dotapparel/api-checkGiftcardBalance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ number, pin })
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || 'Gift card not found')
    }

    return data
  } catch (error) {
    console.error('Error fetching balance:', error)
    throw error
  }
}

function formatDate(date) {
  return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`
}
